/**
 * Reasignar a mano el producto de una fila.
 *
 * POR QUE HACE FALTA
 * ------------------
 * `matchear_lista()` deja en SIN_MATCH todo lo que no reconocio, y esas filas
 * quedan clavadas en IGNORAR: el backend contesta 422 si se las marca ACTUALIZAR
 * sin producto. Sin esta pantalla, un producto que el proveedor renombro se
 * pierde en cada import para siempre.
 *
 * Al elegir un producto, `actualizarFila()` del backend pone `match_tipo` en
 * MANUAL con confianza 1: lo decidio una persona, y eso vale mas que cualquier
 * puntaje de similitud. La vista recalcula sola el precio resultante, asi que
 * despues de elegir hay que releer la revision y no adivinar el numero.
 */
import { useEffect, useState } from "react";

import { Importe } from "@/components/money";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Cargando } from "@/components/ui/cargando";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { EstadoVacio } from "@/components/ui/estado-vacio";
import { InputBusqueda } from "@/components/ui/input-busqueda";
import { useDebounce } from "@/hooks/use-debounce";
import { useProductos } from "@/lib/queries";
import { aNumero } from "@/lib/tipos";
import { cn } from "@/lib/utils";

import type { FilaRevision } from "./logica";

export interface DialogProductoProps {
  /** Lo minimo de una fila: sirve para listas y para compras. */
  fila: Pick<FilaRevision, "descripcion" | "codigo_externo" | "producto_id"> | null;
  abierto: boolean;
  onAbiertoChange: (abierto: boolean) => void;
  /** Se usa como filtro inicial: el producto correcto casi siempre es de él. */
  proveedorId: number | null;
  guardando: boolean;
  onElegir: (productoId: number | null) => void;
  /**
   * Para la compra: con qué arranca el buscador (unas palabras, no el texto
   * entero del remito, que no encontraba nada), todos los proveedores de
   * entrada y también los dados de baja, marcados.
   */
  textoInicial?: string;
  incluirDadosDeBaja?: boolean;
  soloDelProveedorAlAbrir?: boolean;
  descripcion?: string;
}

export function DialogProducto({
  fila,
  abierto,
  onAbiertoChange,
  proveedorId,
  guardando,
  onElegir,
  textoInicial,
  incluirDadosDeBaja = false,
  soloDelProveedorAlAbrir = true,
  descripcion,
}: DialogProductoProps): JSX.Element {
  const [texto, setTexto] = useState("");
  const [soloDelProveedor, setSoloDelProveedor] = useState(soloDelProveedorAlAbrir);

  // Al abrir, se busca lo que dice la lista: es el punto de partida obvio y
  // ahorra tipear el nombre de un producto que ya esta escrito en la pantalla.
  useEffect(() => {
    if (abierto) {
      setTexto(textoInicial ?? fila?.descripcion ?? "");
      setSoloDelProveedor(soloDelProveedorAlAbrir);
    }
  }, [abierto, fila?.descripcion, textoInicial, soloDelProveedorAlAbrir]);

  const consulta = useDebounce(texto, 250).trim();
  const { data, isFetching } = useProductos({
    ...(consulta === "" ? {} : { q: consulta }),
    ...(soloDelProveedor && proveedorId !== null ? { proveedor_id: proveedorId } : {}),
    activo: incluirDadosDeBaja ? "todos" : true,
    limite: 20,
  });

  const productos = data?.filas ?? [];

  return (
    <Dialog open={abierto} onOpenChange={onAbiertoChange}>
      <DialogContent tamano="md">
        <DialogHeader>
          <DialogTitle>Elegir el producto</DialogTitle>
          <DialogDescription>
            {descripcion ?? (
              <>
                La lista dice «{fila?.descripcion ?? ""}»
                {fila?.codigo_externo ? ` (código ${fila.codigo_externo})` : ""}. Elegí a qué
                producto del catálogo le corresponde ese costo.
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-3">
          <InputBusqueda
            valor={texto}
            onValorChange={setTexto}
            etiqueta="Buscar producto"
            placeholder="Buscar por nombre o código"
            cargando={isFetching}
          />

          {proveedorId !== null ? (
            <Checkbox
              checked={soloDelProveedor}
              onChange={(e) => setSoloDelProveedor(e.target.checked)}
              label="Mostrar solo productos de este proveedor"
            />
          ) : null}

          <div className="max-h-80 space-y-1 overflow-y-auto">
            {productos.length === 0 ? (
              isFetching ? (
                <Cargando texto="Buscando…" className="py-4" />
              ) : (
                <EstadoVacio
                  tamano="sm"
                  titulo="No encontramos ningún producto con eso."
                  className="items-center text-center"
                />
              )
            ) : (
              // Cada candidato es un Button outline estirado: la fila entera
              // es el objetivo y el foco se ve, sin un boton nativo a mano.
              productos.map((p) => (
                <Button
                  key={p.id}
                  type="button"
                  variant="outline"
                  disabled={guardando}
                  onClick={() => onElegir(p.id)}
                  className={cn(
                    "h-auto w-full items-start justify-between whitespace-normal px-3 py-2 text-left font-normal sm:h-auto",
                    p.id === fila?.producto_id && "bg-accent",
                  )}
                >
                  <span className="min-w-0">
                    <span className="flex min-w-0 items-center gap-2">
                      <span className="block truncate text-sm font-medium">{p.nombre}</span>
                      {p.activo ? null : <Badge variant="warning">de baja</Badge>}
                    </span>
                    <span className="block truncate text-xs tabular-nums text-muted-foreground">
                      {p.codigo_interno}
                      {p.codigo_externo ? ` · ${p.codigo_externo}` : ""}
                    </span>
                  </span>
                  <span className="flex shrink-0 flex-col items-end text-xs text-muted-foreground">
                    <span className="inline-flex items-baseline gap-1">
                      costo <Importe monto={aNumero(p.costo)} moneda={p.costo_moneda} tamano="sm" />
                    </span>
                    <span className="inline-flex items-baseline gap-1">
                      vende{" "}
                      <Importe
                        monto={aNumero(p.precio_venta)}
                        moneda={p.precio_moneda}
                        tamano="sm"
                      />
                    </span>
                  </span>
                </Button>
              ))
            )}
          </div>
        </DialogBody>

        {/* [Cancelar ghost] [destructiva en el lugar de la primaria] (06 §2.3). */}
        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            disabled={guardando}
            onClick={() => onAbiertoChange(false)}
          >
            Cancelar
          </Button>
          {fila?.producto_id !== null && fila?.producto_id !== undefined ? (
            <Button
              type="button"
              variant="destructive"
              disabled={guardando}
              onClick={() => onElegir(null)}
            >
              Desasociar el producto
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
